import { zodResolver } from '@hookform/resolvers/zod';
import { LogIn } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { FormField } from '@/components/ui/form-field';
import { Input } from '@/components/ui/input';
import { api, mensagemDeErro } from '@/lib/api-client';
import { queryClient } from '@/lib/query-client';
import { loginSchema, type LoginForm } from '@/schemas/login.schema';
import { useAuthStore, type UsuarioSession } from '@/stores/auth.store';

interface LoginResponse {
  // Pós-R-2.17: backend retorna APENAS o usuario. Tokens vivem nos cookies
  // httpOnly setados pelo backend na mesma resposta (ADR 0018).
  usuario: UsuarioSession;
}

export function LoginPage() {
  const navigate = useNavigate();
  const login = useAuthStore((s) => s.login);
  const [erroApi, setErroApi] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
    mode: 'onTouched',
  });

  async function onSubmit(values: LoginForm) {
    setErroApi(null);
    try {
      const { data } = await api.post<LoginResponse>('/auth/login', values);
      // Limpa cache react-query antes de logar — evita herdar dados do
      // usuário anterior (cenário de troca de conta no mesmo browser).
      queryClient.clear();
      // P1-Auth: cookies httpOnly já foram setados pelo backend nesta resposta.
      // O store guarda apenas o usuário (estado UI); a credencial vive no cookie.
      login({ usuario: data.usuario });
      toast.success(`Bem-vindo, ${data.usuario.nome}.`);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      // Mensagem genérica — não distinguir "email inexistente" de "senha
      // errada" (timing-safe da Sprint 03).
      setErroApi(mensagemDeErro(err, 'Email ou senha inválidos.'));
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/40 px-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="rounded-md bg-primary/10 p-2 text-primary">
              <LogIn className="h-5 w-5" />
            </div>
            <div>
              <CardTitle>Entrar</CardTitle>
              <CardDescription>Acesse sua conta de gestão de projetos.</CardDescription>
            </div>
          </div>
        </CardHeader>

        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <CardContent className="space-y-4">
            {erroApi ? (
              <Alert variant="destructive" data-testid="erro-api">
                <AlertDescription>{erroApi}</AlertDescription>
              </Alert>
            ) : null}

            <FormField label="Email" htmlFor="email" required error={errors.email?.message}>
              <Input id="email" type="email" autoComplete="email" {...register('email')} />
            </FormField>

            <FormField label="Senha" htmlFor="senha" required error={errors.senha?.message}>
              <Input
                id="senha"
                type="password"
                autoComplete="current-password"
                {...register('senha')}
              />
            </FormField>
          </CardContent>

          <CardFooter className="flex flex-col items-stretch gap-3">
            <Button type="submit" disabled={isSubmitting} data-testid="botao-entrar">
              {isSubmitting ? 'Entrando…' : 'Entrar'}
            </Button>
            <p className="text-center text-xs text-muted-foreground">
              Ainda não cadastrou a empresa?{' '}
              <Link to="/onboarding" className="underline">
                Fazer cadastro
              </Link>
            </p>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
